import { useEffect, useMemo, useReducer } from 'react';
import { BoxContainer } from '@components/common';
import { Button, CheckboxLabel } from '@components/base';
import FieldHeader from './components/field-header';
import FieldInput from './components/field-input';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { COA_ORDER_DETAILS_DEFAULT } from '@helpers/constants';
import { FormPropsStates } from '@helpers/types/coa';
import css from './index.module.scss';
import { displayProductName, showTrademarkOptions } from '@helpers/utils';

const OrderDetails = (props: FormPropsStates) => {
  const { onFormSubmit, handleCancelClick, orderItem, featureList } = props;
  const { control, handleSubmit, watch, setValue } = useFormContext<any>();

  const showTrademark = useWatch({ name: 'show_trademark', control });
  const showProductName = useWatch({ name: 'show_product_name', control });

  useEffect(() => {
    if (!showTrademark && showProductName) {
      setValue('show_product_name', false);
    }
  }, [showTrademark, showProductName]);

  const { fields, append, remove } = useFieldArray({
    name: 'order_details',
    control
  });

  function generateUniqueId() {
    return `id-${new Date().getTime()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  const orderDetailsWatch = watch('order_details');
  const displayName = useMemo(() => {
    return displayProductName(
      showTrademark,
      showProductName,
      orderItem?.trademark_name,
      orderItem?.order_product_name
    );
  }, [showTrademark, showProductName, orderItem]);

  useEffect(() => {
    const index = orderDetailsWatch?.findIndex((f: any) => f.field_name === 'Item');
    if (index >= 0) {
      setValue(`order_details.${index}.field_value`, displayName);
    }
  }, [displayName, orderDetailsWatch]);

  useEffect(() => {
    if (COA_ORDER_DETAILS_DEFAULT?.length && !fields.length) {
      const defaultFields = COA_ORDER_DETAILS_DEFAULT.map((item: any) => {
        let fieldValue = '';
        if (item === 'Item') {
          fieldValue = orderItem?.order_product_name ?? 'ss';
        } else if (item === 'Batch Number') {
          fieldValue = orderItem?.coa_batch_number;
        }
        return {
          id: generateUniqueId(),
          field_name: item,
          field_value: fieldValue
        };
      });

      append(defaultFields);
    }
  }, []);

  const handleAddField = () => {
    append({
      id: generateUniqueId(),
      field_name: '',
      field_value: ''
    });
  };

  return (
    <form noValidate className={css.coaFormWrapper} onSubmit={handleSubmit(onFormSubmit)}>
      <BoxContainer title="Order Details">
        <div className={css.orderDetailsWrapper}>
          <FieldHeader data={orderDetailsWatch} remove={remove} handleAddField={handleAddField} />
          <FieldInput data={orderDetailsWatch} />
          {showTrademarkOptions(featureList ?? []) ? (
            <>
              <div className={css.nameFieldWrapper}>
                <Controller
                  name="show_trademark"
                  control={control}
                  render={({ field }) => (
                    <CheckboxLabel
                      {...field}
                      disabled
                      label="Show Trademark Name"
                      value={`${field.value}`}
                      checked={field.value}
                    />
                  )}
                />
                <Controller
                  name="show_product_name"
                  control={control}
                  render={({ field }) => (
                    <CheckboxLabel
                      {...field}
                      label="Include Product Name Along with Trademark Name"
                      value={`${field.value}`}
                      checked={field.value}
                    />
                  )}
                />
              </div>
            </>
          ) : (
            <></>
          )}

          <div className={css.formActionWrapper}>
            <Button variant="text" onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button type="submit">Save and Proceed</Button>
          </div>
        </div>
      </BoxContainer>
    </form>
  );
};

export default OrderDetails;
